import { normalizeForDiff } from '@/plugins/diff';

const INDENT = 2;

function pad(level) {
  return ' '.repeat(INDENT * level);
}

function stringifyEntries(entries, space, level) {
  const lines = entries.map((entry, idx) => {
    const val = entry.stringify(space, level + 1);
    const comma = idx < entries.length - 1 ? ',' : '';
    const line = entry.key != null ? `${JSON.stringify(entry.key)}: ${val}` : val;
    return `${pad(level + 1)}${line}${comma}`;
  });
  const open = entries[0]?.key != null ? '{' : '[';
  const close = entries[0]?.key != null ? '}' : ']';
  return `${open}\n${lines.join('\n')}\n${pad(level)}${close}`;
}

function stringifyValue(value, space, level) {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return stringifyEntries(
      value.map(v => ({
        key: null,
        stringify: (s, l) => stringifyValue(v, s, l)
      })),
      space,
      level
    );
  }

  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) return '{}';
    return stringifyEntries(
      keys.map(key => ({
        key,
        stringify: (s, l) => stringifyValue(value[key], s, l)
      })),
      space,
      level
    );
  }

  return JSON.stringify(value);
}

function stringifyNode(node, space, level) {
  if (node.type === 'object') {
    const childs = node.childs || [];
    if (childs.length === 0) return '{}';
    return stringifyEntries(
      childs.map(child => ({
        key: String(child.getField()),
        stringify: (s, l) => stringifyNode(child, s, l)
      })),
      space,
      level
    );
  }

  if (node.type === 'array') {
    const childs = node.childs || [];
    if (childs.length === 0) return '[]';
    return stringifyEntries(
      childs.map(child => ({
        key: null,
        stringify: (s, l) => stringifyNode(child, s, l)
      })),
      space,
      level
    );
  }

  return JSON.stringify(node.getValue());
}

function stringifyJsonEditorTree(editor) {
  const root = editor?.node;
  if (!root) return null;
  if (root.type === 'object' || root.type === 'array') {
    return stringifyNode(root, INDENT, 0);
  }
  return JSON.stringify(root.getValue(), null, INDENT);
}

export function getJsonEditorText(editor, fallback = '') {
  if (!editor) return fallback;
  const mode = editor.getMode();
  if (mode === 'code' || mode === 'text') {
    try {
      return editor.getText();
    } catch {
      return fallback;
    }
  }
  return stringifyJsonEditorTree(editor) ?? fallback;
}

/** Pretty-print a JSON object for upload (Object.keys order). */
export function stringifyJsonObject(obj) {
  return stringifyValue(obj, INDENT, 0);
}

/** Expand minified JSON so line diffs align with editor output. */
export function formatJsonTextForDiff(text) {
  const normalized = normalizeForDiff(text);
  if (!normalized.trim()) return '';
  if (normalized.includes('\n')) return normalized;
  try {
    return stringifyValue(JSON.parse(normalized), INDENT, 0);
  } catch {
    return normalized;
  }
}
